import React, { useEffect, useMemo, useState } from 'react';
import { useFTUE } from '../../contexts/FTUEContext';
import { FTUEHighlighter } from './FTUEHighlighter';

export const FTUEContainer: React.FC = () => {
  const { state, nextStep } = useFTUE();
  const [isTargetReady, setIsTargetReady] = useState(false);
  const currentStep = useMemo(
    () => state.steps[state.currentStepIndex],
    [state.currentStepIndex, state.steps]
  );

  useEffect(() => {
    if (!state.isActive || state.isCompleted || !currentStep?.targetSelector) {
      setIsTargetReady(false);
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 12;

    const resolveTarget = () => {
      if (cancelled) {
        return;
      }

      const element = document.querySelector(currentStep.targetSelector);
      if (element) {
        setIsTargetReady(true);
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        setIsTargetReady(false);
        nextStep();
        return;
      }

      window.setTimeout(resolveTarget, 150);
    };

    setIsTargetReady(false);
    resolveTarget();

    return () => {
      cancelled = true;
      setIsTargetReady(false);
    };
  }, [currentStep, nextStep, state.isActive, state.isCompleted]);

  // Disable body scroll when FTUE is active
  useEffect(() => {
    if (state.isActive && isTargetReady) {
      document.body.style.overflow = 'hidden';
      
      // Disable all interactive elements except FTUE elements
      const allElements = document.querySelectorAll('button, a, input, select, textarea');
      allElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        if (!htmlElement.closest('.ftue-textbox') && !htmlElement.closest('.ftue-highlighted-element')) {
          htmlElement.style.pointerEvents = 'none';
          htmlElement.setAttribute('data-ftue-disabled', 'true');
        }
      });

      return () => {
        document.body.style.overflow = '';
        
        // Re-enable all interactive elements
        const disabledElements = document.querySelectorAll('[data-ftue-disabled="true"]');
        disabledElements.forEach((element) => {
          const htmlElement = element as HTMLElement;
          htmlElement.style.pointerEvents = '';
          htmlElement.removeAttribute('data-ftue-disabled');
        });
      };
    }

    document.body.style.overflow = '';
    const disabledElements = document.querySelectorAll('[data-ftue-disabled="true"]');
    disabledElements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.style.pointerEvents = '';
      htmlElement.removeAttribute('data-ftue-disabled');
    });
  }, [isTargetReady, state.isActive, state.isCompleted]);

  if (!state.isActive || state.isCompleted || !isTargetReady) {
    return null;
  }

  if (!currentStep) {
    return null;
  }

  const isLastStep = state.currentStepIndex === state.steps.length - 1;

  return (
    <FTUEHighlighter
      step={currentStep}
      onClose={nextStep}
      isLastStep={isLastStep}
    />
  );
};
